/**
 * Login Page Logic
 *
 * Handles login form submission and password reset requests
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { initPublicPage, getLoginRedirect } from '../app/init.js';
import { applyTheme } from '../app/theme.js';
import { formSubmitHandler } from '../ui/forms.js';

/**
 * Show the forgot password form
 */
export function showForgotPassword(): void {
  const loginForm = document.getElementById('login-form');
  const forgotForm = document.getElementById('forgot-password-form');
  if (loginForm) loginForm.style.display = 'none';
  if (forgotForm) forgotForm.style.display = 'block';
}

/**
 * Show the login form
 */
export function showLoginForm(): void {
  const loginForm = document.getElementById('login-form');
  const forgotForm = document.getElementById('forgot-password-form');
  if (loginForm) loginForm.style.display = 'block';
  if (forgotForm) forgotForm.style.display = 'none';
}

/**
 * Handle login form submission
 */
async function handleLogin(): Promise<void> {
  const usernameInput = document.getElementById('username') as HTMLInputElement;
  const passwordInput = document.getElementById('password') as HTMLInputElement;
  const errorDiv = document.getElementById('login-error');
  if (errorDiv) errorDiv.textContent = '';

  const username = usernameInput?.value || '';
  const password = passwordInput?.value || '';

  if (!username || !password) {
    if (errorDiv) errorDiv.textContent = 'Please enter username and password';
    return;
  }

  // Get device token from localStorage if available (skip 2FA for trusted devices)
  const deviceToken = localStorage.getItem('vmsman_device_token');

  const result = await apiCall(api.login(username, password, deviceToken || undefined), undefined, (err) => {
    if (errorDiv) errorDiv.textContent = err.message;
  });
  if (!result) return;

  if (result.requires_2fa) {
    // Store TOTP flag and pending token for the 2FA page
    if (result.requires_totp) {
      sessionStorage.setItem('requires_totp', 'true');
    } else {
      sessionStorage.removeItem('requires_totp');
    }
    if (result.pending_token) {
      sessionStorage.setItem('pending_token', result.pending_token);
    }
    // Redirect to 2FA page
    window.location.href = '/2fa';
  } else if (result.session_token) {
    // Login successful (device recognized, 2FA skipped) - redirect
    window.location.href = getLoginRedirect();
  }
}

/**
 * Handle password reset request
 */
async function handleResetRequest(): Promise<void> {
  const emailInput = document.getElementById('reset-email') as HTMLInputElement;
  const messageDiv = document.getElementById('reset-message');
  const resetForm = document.getElementById('reset-form');

  const email = emailInput?.value || '';

  if (!email) {
    if (messageDiv) messageDiv.innerHTML = '<p style="color: red;">Please enter your email address</p>';
    return;
  }

  try {
    await api.requestPasswordReset(email);
  } catch {
    // Intentionally ignore errors - don't reveal if account exists
  }

  // Always show success message (don't reveal if account exists)
  if (messageDiv) {
    messageDiv.innerHTML = '<p style="color: green;">If an account exists with that email, a reset link has been sent.</p>';
  }
  if (resetForm) {
    resetForm.style.display = 'none';
  }
}

/**
 * Initialize the login page
 */
export async function initLoginPage(): Promise<void> {
  applyTheme();

  // Check if already logged in
  const user = await initPublicPage();
  if (user) {
    // Already authenticated - redirect to main page
    window.location.href = getLoginRedirect();
    return;
  }

  // Try proxy-based auto-login (nginx reverse proxy auth via X-Auth-User)
  try {
    const proxyResult = await api.proxyLogin();
    if (proxyResult.session_token) {
      window.location.href = getLoginRedirect();
      return;
    }
  } catch {
    // Not behind a proxy, or proxy auth not configured — show normal form
  }

  // Set up form handlers with loading state
  const loginForm = document.getElementById('login-form-element');
  if (loginForm) {
    formSubmitHandler(loginForm, handleLogin);
  }

  const resetForm = document.getElementById('reset-form');
  if (resetForm) {
    formSubmitHandler(resetForm, handleResetRequest);
  }

  // Show organization description in the page heading if available
  api.getCurrentOrganization().then(org => {
    if (!org?.description) return;
    const heading = document.querySelector('h1.ui-widget-header');
    if (heading) heading.textContent = `VMSMan.IO for ${org.description}`;
    document.title = `VMSMan.IO for ${org.description} - Login`;
  }).catch(() => { /* ignore - org display is optional */ });

}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).showForgotPassword = showForgotPassword;
  (window as unknown as Record<string, unknown>).showLoginForm = showLoginForm;
  (window as unknown as Record<string, unknown>).initLoginPage = initLoginPage;
}
